import { useEffect, useMemo } from 'react'
import type { FC } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { Link, Route, useHistory, useLocation } from 'react-router-dom'
import type { RouteComponentProps } from 'react-router-dom'
import {
	Container,
	Heading,
	Text,
	Box,
	Skeleton,
	SkeletonText,
	Image,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	Button,
	Tag,
	TagLabel,
	Flex,
	Link as ChakraLink,
} from '@chakra-ui/react'
import { Marker } from 'react-map-gl'
import {
	ContentSection,
	ContentSectionHeading,
	SingleLayout,
	SingleHeader,
	SingleHero,
	SingleBody,
	PinIcn,
	Banner,
	BannerAvatar,
	BannerHeading,
	BannerSubHeading,
	IcnClock,
} from '@alexanderathoodly/ui-library'
import {
	SIZE_1070,
	SIZE_410,
	getImgProxyUrl,
	SIZE_1200,
	SIZE_290,
	PLACEHOLDER_SRC,
} from '@yta/image-proxy'
import { PublicPageLayout, ErrorPage } from '~/layouts/pages'
import { getLocationByUrlQuery } from '~/queries/coworking_location'
import { SolutionsDisplay } from './solutions'
import { ServicesIcons } from './services'
import { TestimonialSection } from './testimonials'
import { LocationPageContext } from './context'
import Map from './map'
import { usePersonOfTheDay } from '~/forms/utils'
import { Breadcrumb } from './breadcrumb'
import { GAAction, sendGA4Event } from '~/analytics/ga'
import { useCoworkingSolutionsParams } from './utils'
import type { StatusError } from '@alexanderathoodly/data-models'
import { CountryCode } from '@alexanderathoodly/data-models'
import {
	getFirstBookableDayAndTime,
	RequestShowingButton,
} from '~/forms/showingrequestform'
import { SingleLocationAside, SingleLocationAsideItem } from './aside'
import NotFoundPageLayout from '../notfound/layout'
import { ImgProxy } from '~/utils/imgproxy'
import ProviderSection from './provider'
import SimilarLocationsSection from './similarlocations'
import { DEFAULT_PHONE_NUMBER, PHONE_NUMBER_LINK } from '~/settings'
import { FaqsSection } from './faqs'
import { InfoTable } from './infotable'
import { CoworkinLocationHelmet } from './helmet'
import { getProviderQuery } from './queries'
import { MembershipSection } from './memberships'
import { Temporal } from '@js-temporal/polyfill'
import { capitalizeFirstLetter } from '~/utils/text'
import { useTranslation } from 'react-i18next'
import { useAlternateLinks } from '~/utils/alternatelinks'
import { ConferenceSection } from './conference'
import { useUser } from 'reactfire'
import {
	ShowPriceButton,
	ShowPriceModal,
	useShowPriceModal,
} from '~/forms/showpriceform'

const CoworkingLocationLayout: FC<RouteComponentProps<{ url: string }>> = ({
	match,
}) => {
	const { t, i18n } = useTranslation()
	const alternateLinks = useAlternateLinks('/kontorshotell', match.params.url)
	const params = useCoworkingSolutionsParams()
	const { search } = useLocation()
	const locationResult = useQuery(
		getLocationByUrlQuery(match.params.url, useQueryClient())
	)
	const providerResult = useQuery(
		getProviderQuery(locationResult.data?.provider)
	)
	const { status: userStatus, data: user } = useUser()
	const { isShowPriceModalOpen, onShowPriceModalClose, onShowPriceModalOpen } =
		useShowPriceModal('conversion-box', locationResult.data?.id || '')
	const history = useHistory()
	const todaysExpert = usePersonOfTheDay()
	const imageElems = useMemo(() => {
		if (locationResult.data == undefined) {
			return []
		}
		return locationResult.data.images.map((src) => (
			<ImgProxy
				key={src}
				src={src}
				sizeSet={{ base: SIZE_290, md: SIZE_410, lg: SIZE_1070 }}
				w="100%"
				alt={
					locationResult.data.business_area
						? `kontorshotell i ${
								locationResult.data.business_area.split('/')[0] || ''
						  } - ${locationResult.data.name}`
						: `kontorshotell - ${locationResult.data.name}`
				}
			/>
		))
	}, [locationResult.data])

	useEffect(() => {
		const container = document.getElementById('membership') // We could pass ref of useRef, but the error "Function components cannot be given refs." occurs. It is easier to use getElementById.
		if (container && params.type == 'membership') {
			container.scrollIntoView()
		}
	}, [params.type])

	if (locationResult.isLoading || locationResult.isIdle) {
		return (
			<PublicPageLayout>
				<Container maxW="container.lg" minH={2000}>
					<SingleLayout>
						<SingleHeader>
							<Skeleton h={10} />
						</SingleHeader>
						<SingleHero position="relative">
							<Skeleton h={52} />
						</SingleHero>
						<SingleBody>
							<SkeletonText noOfLines={6} spacing={4} mb={14} />
							<SkeletonText noOfLines={6} spacing={4} />
						</SingleBody>
					</SingleLayout>
				</Container>
			</PublicPageLayout>
		)
	}
	if (locationResult.isError) {
		if ((locationResult.error as StatusError).statusCode == 404) {
			return <NotFoundPageLayout />
		}
		return <ErrorPage error={locationResult.error} />
	}
	const location = locationResult.data
	const openedDate = location.opened && Temporal.PlainDate.from(location.opened)
	const today = Temporal.Now.plainDateTime('iso8601', 'Europe/Stockholm')
	const firstBookableDay = getFirstBookableDayAndTime(today, location)
	return (
		<PublicPageLayout>
			<LocationPageContext.Provider value={{ location }}>
				<CoworkinLocationHelmet location={location}>
					<title>{`${t('LocationPageMetaTitle', {
						ns: 'meta_data',
						location_name: location.name,
					})}`}</title>
					<meta
						name="description"
						content={
							i18n.language != 'en'
								? location.short_description
								: location.short_description_en
						}
					/>
					<meta
						name="robots"
						content={
							search || location.address.country == CountryCode.DENMARK
								? 'noindex, follow'
								: 'index, follow'
						}
					/>
					{location.images[0] && (
						<meta
							property="og:image"
							content={getImgProxyUrl(location.images[0], SIZE_1200)}
						/>
					)}
					<meta
						property="og:image:width"
						content={SIZE_1200.width.toString()}
					/>
					<meta
						property="og:image:height"
						content={SIZE_1200.height.toString()}
					/>
					{alternateLinks &&
						alternateLinks.map((link) => (
							<link
								key={link.url}
								rel="alternate"
								hrefLang={link.lng}
								href={link.url}
							/>
						))}
					{alternateLinks && (
						<link
							rel="alternate"
							hrefLang="x-default"
							href={alternateLinks.find((link) => link.lng == 'sv')!.url}
						/>
					)}
					{alternateLinks && (
						<link
							rel="canonical"
							href={
								alternateLinks.find((link) => link.lng == i18n.language)!.url
							}
						/>
					)}
				</CoworkinLocationHelmet>
				<Container maxW="container.lg" minH={2000}>
					<SingleLayout
						gridTemplateRows="min-content 5.5rem 5.5rem 1fr"
						gridTemplateColumns={{ base: '1fr', lg: '66% 1fr' }}
					>
						<SingleHeader>
							<Heading as="h1">{location.name}</Heading>
							<Text color="grey.500" mt={1}>
								{t(
									'Se alla lediga kontor på kontorshotellet {{location_name}}.',
									{
										location_name: location.name,
									}
								)}
							</Text>
							<Flex
								flexDirection={{ base: 'column', md: 'row' }}
								justifyContent="space-between"
								gap="0.5rem"
							>
								<Breadcrumb location={location} />
								<Box>
									{location.highlights.map((highlight) => (
										<Tag
											key={highlight}
											backgroundColor="blue.50"
											borderRadius="full"
											ml={1}
										>
											<TagLabel my={2}>{t(highlight)}</TagLabel>
										</Tag>
									))}
								</Box>
							</Flex>
						</SingleHeader>
						<SingleHero position="relative" height="fit-content">
							<ImgProxy
								src={location.images[0] || PLACEHOLDER_SRC}
								sizeSet={{ base: SIZE_290, md: SIZE_410, lg: SIZE_1070 }}
								w="100%"
								css={{ aspectRatio: '18 / 10' }}
								alt={t('kontorshotell i {{city}} - {{location_name}}', {
									ns: 'meta_data',
									city: location.address.city,
									location_name: location.name,
								})}
							/>
							<Button
								as={Link}
								to={`${match.url}/bilder${history.location.search}`}
								replace={true}
								variant="outline"
								bg="white"
								bottom={4}
								left={4}
								position="absolute"
							>
								{t('Alla bilder')}
							</Button>
						</SingleHero>
						<SingleLocationAside
							display={{ base: 'none', lg: 'block' }}
							bottom="-100px"
						>
							<SingleLocationAsideItem>
								<Box
									display={{ base: 'none', lg: 'grid' }}
									gridTemplate={{
										base: 'auto auto / auto auto',
										lg: 'repeat(3, auto) / 1fr',
									}}
								>
									<Heading
										mb={2}
										fontWeight="semibold"
										color="grey.800"
										fontSize="2xl"
									>
										{t('Intresserad av kontoret?')}
									</Heading>
									<Text mb={5} color="blackAlpha.700" fontSize="md">
										{t(
											'Gå på en rundvandring av kontoret - önska dag och tid i nästa steg.'
										)}
									</Text>
									<Flex
										alignItems="center"
										gap={3}
										h={10}
										bgColor="blue.50"
										mb={4}
										p={2}
									>
										<IcnClock
											width={5}
											height={5}
											display={{ base: 'none', md: 'inline-block' }}
										/>
										<Text
											color="blackAlpha.700"
											fontSize="xs"
											fontStyle="italic"
										>
											{`${t('Första möjliga tid')}: ${
												Temporal.PlainDate.compare(firstBookableDay, today) ===
												0
													? `${t('Idag')} ${firstBookableDay
															.round({
																roundingMode: 'floor',
																smallestUnit: 'hour',
															})
															.toLocaleString(`${i18n.language}-SE`, {
																hour: 'numeric',
																minute: 'numeric',
															})}`
													: firstBookableDay
															.round({
																roundingMode: 'floor',
																smallestUnit: 'hour',
															})
															.toLocaleString(`${i18n.language}-SE`, {
																weekday: 'long',
																hour: 'numeric',
																minute: 'numeric',
															})
											}`}
										</Text>
									</Flex>
									<RequestShowingButton
										location={location}
										workspaces={params.workspaces}
										mb={3}
									/>
									{userStatus == 'loading' ? (
										<Skeleton w="100%" h={10} borderRadius="md" />
									) : (
										!user && (
											<ShowPriceButton
												GA4EventLocation="conversion box"
												onClick={onShowPriceModalOpen}
											>
												{t('Begär offert')}
											</ShowPriceButton>
										)
									)}
								</Box>
							</SingleLocationAsideItem>
							<Text mx={3} textAlign="center" color="grey.500" fontSize="md">
								{t('Har du frågor om kontoret? Ring oss på')}{' '}
								<ChakraLink
									color="blue.500"
									fontWeight="semibold"
									href={PHONE_NUMBER_LINK}
									onClick={() =>
										sendGA4Event('click_phone_number', {
											operator:
												providerResult.data?.name ||
												'failed_to_get_operator_name', // We don't want to affect the app even if fetching provider for GA fails
											location: location.name,
										})
									}
								>
									{DEFAULT_PHONE_NUMBER}
								</ChakraLink>
								.
							</Text>
						</SingleLocationAside>
						<SingleBody>
							<Route exact path={`${match.path}/bilder`}>
								<Modal
									isOpen={true}
									onClose={() =>
										history.replace(`${match.url}${history.location.search}`)
									}
								>
									<ModalOverlay />
									<ModalContent maxW="6xl">
										<ModalHeader>{location.name}</ModalHeader>
										<ModalCloseButton />
										<ModalBody display="flex" flexDirection="column" gap="50px">
											{imageElems}
										</ModalBody>
									</ModalContent>
								</Modal>
							</Route>
							<ContentSection
								display="flex"
								gap={6}
								flexWrap="wrap"
								borderTop="none"
								pt={{ base: 0, md: 7 }}
							>
								<Box>
									<Text
										fontWeight="semibold"
										fontSize={{ base: 'xl', md: '2xl' }}
									>
										{location.size}m²
									</Text>
									<Text fontSize="medium" color="grey.500">
										{t('Anläggnings storlek')}
									</Text>
								</Box>
								{location.number_of_meeting_rooms > 0 && (
									<Box>
										<Text
											fontWeight="semibold"
											fontSize={{ base: 'xl', md: '2xl' }}
										>
											{location.number_of_meeting_rooms}
										</Text>
										<Text fontSize="medium" color="grey.500">
											{t('Mötesrum')}
										</Text>
									</Box>
								)}
								{openedDate && (
									<Tag
										borderRadius="full"
										height="max-content"
										alignSelf="center"
										py={3}
										px={5}
									>
										{Temporal.PlainDate.compare(openedDate, today) > 0
											? t('Öppnar')
											: t('Öppnades')}{' '}
										{capitalizeFirstLetter(
											openedDate.toLocaleString(`${i18n.language}-SE`, {
												year: 'numeric',
												month: 'short',
											})
										)}
									</Tag>
								)}
								{location.is_premium_listing && providerResult.data && (
									<ChakraLink
										href={`${t('/operatörer', { ns: 'url' })}/${
											location.provider
										}`}
									>
										<ImgProxy
											src={providerResult.data.logo}
											sizeSet={{ base: SIZE_290 }}
											maxH="6rem"
											maxW="6rem"
										/>
									</ChakraLink>
								)}
							</ContentSection>
							<ContentSection>
								<ContentSectionHeading>
									{t('Bekvämligheter')}
								</ContentSectionHeading>
								<ServicesIcons services={location.services} />
							</ContentSection>
							<ContentSection pb={2}>
								<ContentSectionHeading>
									{t('Tillgängliga kontor')}
								</ContentSectionHeading>
								<Text pb={3}>
									{t(
										'Kontor och egna avdelningar tillgängliga inom 6 månader på {{location_name}}. 3 - 5m² kontorsyta per arbetsplats rekommendras. För fler tillgängliga kontor på {{location_name}} vänligen ta kontakt med våra kontorsexperter som hjälper er med en skräddarsydd offert.',
										{
											location_name: location.name,
										}
									)}
								</Text>
								<SolutionsDisplay />
							</ContentSection>
							<MembershipSection locationName={location.name} />
							<ConferenceSection />
							<ContentSection>
								<ContentSectionHeading>
									{`${t('Om')} ${
										location.name
									}${`, ${location.breadcrumbs.area.name}`}`}
								</ContentSectionHeading>
								<Text whiteSpace="pre-wrap" color="grey.500">
									{i18n.language != 'en'
										? location.description
										: location.description_en}
								</Text>
							</ContentSection>
							<InfoTable location={location} my={2} />
							<ProviderSection url={match.params.url} />
							<TestimonialSection />
							<ContentSection>
								<ContentSectionHeading>{t('Området')}</ContentSectionHeading>
								<Map
									latitude={location.address.coordinates.lat}
									longitude={location.address.coordinates.lng}
									css={{ contentVisibility: 'auto' }}
								>
									<Marker
										latitude={location.address.coordinates.lat}
										longitude={location.address.coordinates.lng}
									>
										<Box as="button">
											<PinIcn
												height={10}
												width={7}
												outer={{
													fill: 'blue.500',
													stroke: 'blue.500',
												}}
												inner={{ fill: 'white' }}
											/>
										</Box>
									</Marker>
								</Map>
							</ContentSection>
							<ContentSection>
								<Box as={Link} to={t('/kontorshjalpen', { ns: 'url' })}>
									<Banner
										variant="horizontal"
										size="lg"
										onClick={() => {
											GAAction({
												category: 'engagement',
												action: 'click_expert_banner',
												label: 'expert',
											})
											sendGA4Event('click_kontorshjalpen', {
												page_type: 'location',
												placement: 'content',
												placement_type: 'banner',
												content: 'expert',
											})
										}}
									>
										<BannerAvatar>
											<Image
												src={todaysExpert.img}
												borderRadius="full"
												minWidth="80px"
												alt={todaysExpert.name}
											/>
										</BannerAvatar>
										<BannerHeading>
											{t(
												'Behöver du hjälp att hitta rätt lösning? Boka ett gratis rådgivningsmöte.'
											)}
										</BannerHeading>
										<BannerSubHeading>
											<strong>{todaysExpert.name}</strong>,{' '}
											{t(todaysExpert.positionTitle)}. 100% {t('kostnadsfritt')}
										</BannerSubHeading>
									</Banner>
								</Box>
							</ContentSection>
							<FaqsSection my="2rem" />
						</SingleBody>
					</SingleLayout>
					<SimilarLocationsSection url={match.params.url} />
				</Container>
				<SingleLocationAside
					display={{ lg: 'none' }}
					px={3}
					py={3}
					w="100%"
					borderTop="1px"
					borderColor="grey.100"
					background="white"
					zIndex={2}
				>
					<Text mb={3} fontWeight="semibold">
						{t('Intresserad av det här kontoret?')}
					</Text>
					<Flex>
						{userStatus == 'loading' ? (
							<Skeleton w="100%" h={10} borderRadius="md" />
						) : (
							!user && (
								<ShowPriceButton
									GA4EventLocation="conversion box"
									onClick={onShowPriceModalOpen}
									mr={5}
									width="100%"
								>
									{t('Begär offert')}
								</ShowPriceButton>
							)
						)}
						<RequestShowingButton
							location={location}
							workspaces={params.workspaces}
						/>
					</Flex>
				</SingleLocationAside>
				<ShowPriceModal
					location={location}
					isOpen={isShowPriceModalOpen}
					onClose={onShowPriceModalClose}
				/>
			</LocationPageContext.Provider>
		</PublicPageLayout>
	)
}

export default CoworkingLocationLayout
